import { IMG } from "@/lib/data";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal, StaggerContainer, StaggerItem } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

function HerdHero() {
  return (
    <section className="relative bg-bone text-ink pt-40 md:pt-48 pb-16">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14">
        <RevealWords
          text="— The Herd · Purebreds & Hybrid Cross"
          className="font-mono text-[11px] tracking-overline uppercase text-sage"
        />
        <RevealLines
          as="h1"
          lines={["Registered purebreds", "& hybrid cross cattle."]}
          className="mt-8 font-serif text-[12vw] md:text-[8.2vw] lg:text-[7vw] leading-[0.92] tracking-tightest text-ink"
          delay={0.15}
          stagger={0.13}
        />
        <Reveal delay={0.35} className="mt-8 max-w-2xl">
          <p className="text-ink/75 text-lg leading-relaxed">
            A small, deliberate herd on the Bitterroot — Registered Black Angus
            and Charolais purebreds, plus hybrid crosses bred for the best of
            both. Every cow earns her place. Every bull leaves with a job to do.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function BreedSection({
  breed,
  tagline,
  body,
  img,
  imgAlt,
  reverse = false,
  pos = "object-center",
}) {
  return (
    <section className="relative bg-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div
          className={`grid grid-cols-12 gap-8 md:gap-12 items-center ${
            reverse ? "" : ""
          }`}
        >
          <Reveal
            className={`col-span-12 md:col-span-7 ${
              reverse ? "md:order-2" : ""
            }`}
            y={30}
          >
            <div className="clip-editorial-tall md:aspect-[4/5] relative overflow-hidden">
              <img
                src={img}
                alt={imgAlt}
                className={`h-full w-full object-cover ${pos}`}
                loading="lazy"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
              {breed}
            </p>
          </Reveal>

          <Reveal
            className={`col-span-12 md:col-span-5 ${
              reverse ? "md:order-1 md:pr-6" : "md:pl-6"
            }`}
            y={30}
            delay={0.05}
          >
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              {tagline}
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
              {breed}
            </h2>
            <p className="mt-6 text-ink/80 leading-relaxed text-lg font-light max-w-[52ch]">
              {body}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Herd() {
  return (
    <div className="bg-bone">
      <HerdHero />
      <EditorialMarquee
        phrases={[
          "Registered Black Angus",
          "Registered Charolais",
          "Hybrid Cross Cattle",
          "Structural Correctness",
          "Disposition",
        ]}
      />
      <BreedSection
        breed="Registered Black Angus"
        tagline="Maternal · Marbling · Docility"
        body="Our Angus program is the maternal foundation of the ranch. Females are selected hardest for sound feet and legs, well-attached udders, fertility and disposition — with carcass and growth as a compounding second. Bred to sires that leave functional, problem-free calves worth keeping."
        img={IMG.angusAutumn}
        imgAlt="Registered Black Angus cow in a Bitterroot pasture, autumn cottonwoods behind"
        pos="object-[35%_center]"
      />
      <BreedSection
        breed="Registered Charolais"
        tagline="Growth · Muscle · Temperament"
        body="Our Charolais bring gentle temperament, growth and muscle to any cross. Selected for feet and legs first — cattle that can walk to water and cover ground in Montana winters."
        img={IMG.charolaisFlagship}
        imgAlt="Registered Charolais cow — professional side profile"
        reverse
        pos="object-[15%_center]"
      />
      <BreedSection
        breed="Hybrid Cross Cattle"
        tagline="Hybrid Vigor · Growth · Efficiency"
        body="Where our two purebred programs meet: Angus-Charolais crosses that capture true hybrid vigor. Smoke-grey and baldy calves with the maternal instinct of the Angus and the growth and yield of the Charolais — efficient, hardy females and hard-hitting feeder cattle."
        img={IMG.heroSunsetPasture}
        imgAlt="Black Angus and white Charolais cattle grazing together at sunset"
      />
      {/* The land — full-bleed vista interlude */}
      <section className="relative h-[62svh] md:h-[82svh] w-full overflow-hidden bg-obsidian">
        <img
          src={IMG.pastureVista}
          alt="Bitterroot Valley pasture with ranch buildings and distant mountains"
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-obsidian/30" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 flex items-end pb-14 md:pb-20">
          <Reveal className="max-w-xl" y={40}>
            <p className="font-mono text-[11px] tracking-overline uppercase text-bone/85">
              — The Land
            </p>
            <p className="mt-4 font-serif text-4xl md:text-6xl leading-[1] tracking-tight text-bone">
              Good ground makes <br />
              <span className="italic">good cattle.</span>
            </p>
          </Reveal>
        </div>
      </section>
      <section className="relative bg-parchment">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal className="col-span-12 md:col-span-8">
              <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
                — Continue
              </p>
              <h3 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight text-ink leading-[1]">
                See our bulls <br />
                <span className="italic text-sage">and heifers.</span>
              </h3>
            </Reveal>
            <Reveal
              className="col-span-12 md:col-span-4 flex flex-col gap-3"
              y={20}
            >
              <Link
                to="/bulls"
                className="inline-flex items-center justify-between px-6 py-4 bg-ink text-bone text-[12px] uppercase tracking-overline hover:bg-terracotta transition-colors"
              >
                <span>Bulls</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/heifers"
                className="inline-flex items-center justify-between px-6 py-4 border border-ink text-ink text-[12px] uppercase tracking-overline hover:bg-ink hover:text-bone transition-colors"
              >
                <span>Replacement Heifers</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-between px-6 py-4 border border-ink text-ink text-[12px] uppercase tracking-overline hover:bg-ink hover:text-bone transition-colors"
              >
                <span>Inquire</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
